import { useState } from "react";
import { Archive, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAttention } from "@/context/AttentionContext";

// Shared bulk Archive / Delete Permanently bar + confirmation, reused by the
// custom (non-CrudResource) list pages. One bulk API call; partial failures
// are surfaced; attention counts refresh afterwards.
export default function BulkActions({ endpoint, ids, singular, title, canArchive = true, canPurge = true, onDone, clear }) {
  const [confirm, setConfirm] = useState(null);
  const attention = useAttention();
  if (!ids.length) return null;

  const run = async () => {
    const path = confirm.mode === "purge" ? `/${endpoint}/bulk-delete` : `/${endpoint}/bulk-archive`;
    try {
      const { data } = await api.post(path, { ids });
      const done = confirm.mode === "purge" ? data.deleted : data.archived;
      const failed = data.failed || [];
      const verb = confirm.mode === "purge" ? "deleted" : "archived";
      if (failed.length) toast.warning(`${done} ${verb}. ${failed.length} could not be ${verb}${failed[0]?.reason ? `: ${failed[0].reason}` : ""}.`);
      else toast.success(`${done} ${singular}${done === 1 ? "" : "s"} ${verb}.`);
      setConfirm(null); clear?.(); onDone?.(); attention.refresh();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#00C9E0]/30 bg-[#00C9E0]/5 px-4 py-2.5" data-testid="bulk-bar">
        <span className="text-sm font-semibold text-slate-700" data-testid="bulk-count">{ids.length} selected</span>
        <div className="flex-1" />
        {canArchive && <Button variant="outline" size="sm" onClick={() => setConfirm({ mode: "archive" })} data-testid="bulk-archive"><Archive className="h-4 w-4" />Archive</Button>}
        {canPurge && <Button variant="outline" size="sm" className="border-[#C42607]/40 text-[#C42607] hover:bg-[#C42607]/10" onClick={() => setConfirm({ mode: "purge" })} data-testid="bulk-delete"><Trash2 className="h-4 w-4" />Delete Permanently</Button>}
        <Button variant="ghost" size="sm" onClick={clear} data-testid="bulk-clear">Clear Selection</Button>
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          {confirm && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirm.mode === "purge" ? `Permanently delete ${ids.length} ${title.toLowerCase()}?` : `Archive ${ids.length} ${title.toLowerCase()}?`}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirm.mode === "purge"
                    ? "These records will be permanently removed from the CRM. Protected records (financial/history) will be skipped and reported. This action cannot be undone."
                    : "They will be hidden from active lists but preserved and restorable by an admin."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={run} className={confirm.mode === "purge" ? "bg-[#C42607] hover:bg-[#9e1e06]" : "bg-[#FE421C] hover:bg-[#C42607]"} data-testid={confirm.mode === "purge" ? "confirm-delete" : "confirm-archive"}>
                  {confirm.mode === "purge" ? `Delete ${ids.length} Permanently` : `Archive ${ids.length}`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
